import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { contactsApi } from '../../api/contacts-api';
import { useAuth } from '../../auth/AuthContext';
import {
  contactName,
  contactPurposeLabels,
  isStaffContact,
  type Contact,
  type ContactPurpose,
  type ContactResolution,
} from '../../domain/contact';
import type { Site } from '../../domain/inventory';
import { siteLabel } from '../../lib/site-label';
import { ContactDrawer } from './ContactDrawer';

/** Shown even when nobody is set, so the gap is visible. */
const ALWAYS_SHOWN: readonly ContactPurpose[] = ['OPERATIONS', 'RECEIVING', 'CUSTOMER_SUCCESS'];

function levelNote(resolution: ContactResolution, site: Site): string | undefined {
  switch (resolution.level) {
    case 'REGION':
      return `for region ${site.labels?.region ?? ''}`.trim();
    case 'CUSTOMER':
      return 'for the whole customer';
    default:
      return undefined;
  }
}

/**
 * Who to talk to at a site, per purpose (tidaltech/koi#8359): the site's own
 * contacts, else its region's, else the customer's.
 */
export function SiteContactsPanel({
  site,
  customerSites,
  customerName,
}: {
  site: Site;
  customerSites: readonly Site[];
  customerName: string;
}) {
  const { canMutate } = useAuth();
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const resolutions = useQuery({
    queryKey: ['contacts', 'resolve', site.id],
    queryFn: () => contactsApi.resolveContacts(site.id),
  });

  const shown = (resolutions.data ?? []).filter(
    (resolution) => resolution.contacts.length > 0 || ALWAYS_SHOWN.includes(resolution.purpose),
  );

  return (
    <section className="site-contacts" aria-label={`Contacts for ${siteLabel(site)}`}>
      <div className="site-contact-heading">
        <strong>Contacts</strong>
        <span className="site-contacts-links">
          <Link to={`/customers/${encodeURIComponent(site.customerId)}?tab=contacts`}>
            All {customerName} contacts
          </Link>
          <button
            type="button"
            className="link-button"
            onClick={() => setEditing('new')}
            disabled={!canMutate}
            title={canMutate ? undefined : 'Sign in to add contacts'}
          >
            Add<span className="visually-hidden"> a contact for {siteLabel(site)}</span>
          </button>
        </span>
      </div>
      {resolutions.isLoading ? <p className="empty-note">Loading contacts…</p> : null}
      {resolutions.isError ? (
        <p className="empty-note">
          Contacts could not be loaded.{' '}
          <button type="button" className="link-button" onClick={() => void resolutions.refetch()}>
            Try again
          </button>
        </p>
      ) : null}
      {resolutions.isSuccess ? (
        <dl className="site-contacts-list">
          {shown.map((resolution) => (
            <div key={resolution.purpose}>
              <dt>{contactPurposeLabels[resolution.purpose]}</dt>
              <dd>
                {resolution.contacts.length === 0 ? (
                  <span className="contact-muted">Nobody set</span>
                ) : (
                  resolution.contacts.map((contact, index) => (
                    <span key={contact.id} className="site-contact-person">
                      {index > 0 ? ', ' : null}
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => setEditing(contact)}
                        disabled={!canMutate}
                      >
                        {contactName(contact)}
                      </button>
                      {isStaffContact(contact) ? ' (Tidal)' : null}
                      {contact.phone ? ` · ${contact.phone}` : null}
                      {contact.email ? (
                        <>
                          {' · '}
                          <a href={`mailto:${contact.email}`}>{contact.email}</a>
                        </>
                      ) : null}
                    </span>
                  ))
                )}
                {resolution.contacts.length > 0 && levelNote(resolution, site) ? (
                  <span className="contact-muted"> — {levelNote(resolution, site)}</span>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {editing ? (
        <ContactDrawer
          customerId={site.customerId}
          customerName={customerName}
          sites={customerSites}
          contact={editing === 'new' ? undefined : editing}
          initialScope={
            editing === 'new'
              ? { level: 'SITE', siteId: site.id, purposes: ['OPERATIONS', 'RECEIVING'] }
              : undefined
          }
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}
